import React from 'react';
import { motion } from 'framer-motion';
import { Radar, ArrowRight, Users, Bell, ScrollText, ShieldCheck, KeyRound, BellOff, Clock } from 'lucide-react';
import RadarScope from './RadarScope';

const FOLLOWER_RADAR_URL = 'https://followerradar.com';

interface RadarFeature {
    label: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

// 팔로워 레이더가 감지·제공하는 것
const features: RadarFeature[] = [
    { label: '팔로워·팔로잉 명단 추출', icon: ScrollText },
    { label: '실시간 변화 감지', icon: Users },
    { label: '실시간 문자 알림', icon: Bell },
    { label: '아이디·비번 불필요', icon: KeyRound },
    { label: '6시간~30분 주기', icon: Clock },
    { label: '100% 비밀 보장', icon: ShieldCheck },
];

// 보안·프라이버시 신뢰 배지
const trustBadges: RadarFeature[] = [
    { label: '인스타 아이디·비밀번호 안 받음', icon: KeyRound },
    { label: '상대방은 알 수 없어요', icon: BellOff },
    { label: '100% 비밀 보장', icon: ShieldCheck },
];

/**
 * 랜딩(HomePage)용 팔로워 레이더 유입 섹션.
 * MASK LAB(FaceAnalysisCTA) 형제 컴포넌트로, 인스타 전문 서비스라 그 위에 배치한다.
 * SocialDataLab 글래스 골격은 유지하되 레이더 브랜드(teal/mint) 액센트로 "다른 서비스로의 이동"을 신호한다.
 * 클릭 시 followerradar.com 랜딩으로 이동.
 */
const FollowerRadarCTA: React.FC = () => {
    return (
        <section className="relative overflow-hidden py-12 sm:py-16">
            {/* 배경 글로우 */}
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#02d1ab]/10 blur-[120px]" />

            <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
                <motion.a
                    href={FOLLOWER_RADAR_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    whileHover={{ y: -4 }}
                    className="group relative block overflow-hidden rounded-3xl border border-[#1f3b38] bg-gradient-to-br from-[#0A0A0A] via-[#0a0f0e] to-[#0A0A0A] p-6 shadow-[0_0_60px_rgba(2,209,171,0.08)] transition-shadow hover:shadow-[0_0_72px_rgba(2,209,171,0.16)] sm:p-10 lg:p-12"
                >
                    {/* 카드 내부 데코 글로우 */}
                    <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#02d1ab]/10 blur-[100px]" />
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#02d1ab]/45 to-transparent" />

                    <div className="relative grid items-center gap-10 lg:grid-cols-2">
                        {/* 좌측: 카피 */}
                        <div className="flex min-w-0 flex-col items-center gap-5 text-center lg:items-start lg:text-left">
                            <span className="inline-flex items-center gap-2 rounded-full border border-[#02d1ab]/25 bg-[#0c1413]/80 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#7fe9cf] backdrop-blur-sm sm:px-5 sm:py-2 sm:text-xs">
                                <Radar className="h-3 w-3 text-[#6effb0] sm:h-4 sm:w-4" />
                                관심 계정 알림 · FOLLOWER RADAR
                            </span>

                            <h2 className="text-[22px] font-bold leading-snug tracking-[-0.01em] break-keep text-white sm:text-[26px] md:text-[30px] lg:text-[32px]">
                                그 계정이 누굴 팔로우하고,<br className="hidden lg:block" /> 누가 그 계정을 팔로우하는지{' '}
                                <span className="bg-gradient-to-r from-[#02d1ab] to-[#6effb0] bg-clip-text text-transparent">
                                    실시간으로 알려드립니다
                                </span>
                            </h2>

                            <p className="max-w-xl text-sm leading-relaxed text-[#A8A8A8] sm:text-base">
                                AI와 자동화 기술로 인스타 계정의 팔로워·팔로잉 변화를 모두 24시간 감지해, 변화가 생기는 순간 문자로 알려드려요.
                                <span className="text-white font-semibold"> 팔로워·팔로잉 전체 명단까지 추출</span>해 전달하고,
                                인스타 아이디·비밀번호는 받지 않아요.
                            </p>

                            <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#02d1ab] to-[#14b8a6] px-6 py-3 text-sm font-semibold text-[#04110e] shadow-[0_0_30px_rgba(2,209,171,0.3)] transition-all group-hover:shadow-[0_0_44px_rgba(2,209,171,0.5)]">
                                팔로워 레이더 시작하기
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </span>

                            {/* 보안·비밀 신뢰 배지 */}
                            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 lg:justify-start">
                                {trustBadges.map((b) => (
                                    <span key={b.label} className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#7fe9cf] sm:text-xs">
                                        <b.icon className="h-3.5 w-3.5 shrink-0 text-[#6effb0]" />
                                        {b.label}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* 우측: 레이더 스코프 + 감지 항목 칩 */}
                        <div className="relative mx-auto flex w-full max-w-sm flex-col items-center gap-6">
                            <RadarScope size={232} className="sm:scale-110" />

                            <div className="grid w-full grid-cols-2 gap-2.5">
                                {features.map((f, i) => (
                                    <motion.div
                                        key={f.label}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: 0.15 + i * 0.07 }}
                                        className="flex items-center gap-2 rounded-xl border border-[#1f3b38] bg-[#0c1413]/80 px-3 py-2 backdrop-blur-sm"
                                    >
                                        <f.icon className="h-3.5 w-3.5 shrink-0 text-[#6effb0]" />
                                        <span className="truncate text-[11px] font-medium text-[#A8A8A8] sm:text-xs">{f.label}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.a>
            </div>
        </section>
    );
};

export default FollowerRadarCTA;
